import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Collapse,
  Empty,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  FormOutlined,
  NodeIndexOutlined,
  PhoneOutlined,
  TwitterOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatAppDateTime } from "util/formatAppDate";
import { useSelector } from "react-redux";

import WelcomeDrawerSimpleReviewCard from "pages/Welcome/WelcomeDrawerSimpleReviewCard";
import { useWelcomeDrawerReviewHighlights } from "pages/Welcome/useWelcomeDrawerReviewHighlights";
import SentimentSparkline from "./SentimentSparkline";
import ReviewAnalysisPanel, {
  aggregateReviewAnalysis,
} from "./ReviewAnalysisPanel";
import {
  acknowledgeRecommendation,
  fetchProfile,
  fetchRecommendations,
  fetchTimeline,
  listProfiles,
  mergeProfiles,
  startOutreach,
} from "./service";
import styles from "./style.module.scss";
import {
  buildTimelineDrawerReviews,
  timelineItemToDrawerReview,
} from "./timelineDrawerReview";
import { resolveApiErrorMessage } from "util/apiErrorMessage";

function channelIcon(item) {
  const payload = item?.raw_payload || {};
  const detail = item?.review_detail || payload.review_detail || {};
  if (
    payload.platform === "ets_reviews" ||
    detail.review_type ||
    /^ACS_|^SURVEY_|^OTELPUAN_/i.test(
      String(detail.ticket_id || payload.ticket_id || "")
    )
  ) {
    return <FormOutlined />;
  }
  if (item?.source_channel === "survey") return <FormOutlined />;
  if (item?.source_channel === "social_media") return <TwitterOutlined />;
  return <NodeIndexOutlined />;
}

const ProfileDetailView = ({ profileRef, embedded = false }) => {
  const { t, i18n } = useTranslation();
  const theme = useSelector((state) => state.appReducer?.themeName) || "light";
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelinePage, setTimelinePage] = useState(1);
  const [recommendations, setRecommendations] = useState([]);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState("");
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [outreachBusy, setOutreachBusy] = useState(null);

  const load = useCallback(async () => {
    if (!profileRef) return;
    setLoading(true);
    try {
      const [pRes, tRes, rRes] = await Promise.all([
        fetchProfile(profileRef),
        fetchTimeline(profileRef, { page: 1, pageSize: 25 }),
        fetchRecommendations(profileRef),
      ]);
      if (!pRes || pRes.error) {
        message.error(
          resolveApiErrorMessage(pRes, t, "customer_365.load_error")
        );
        setProfile(null);
        return;
      }
      setProfile(pRes.profile || null);
      setTimeline(tRes && !tRes.error ? tRes : null);
      setTimelinePage(1);
      setRecommendations(
        rRes && !rRes.error ? rRes.recommendations || [] : []
      );
    } finally {
      setLoading(false);
    }
  }, [profileRef, t]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMoreTimeline = async () => {
    const next = timelinePage + 1;
    const tRes = await fetchTimeline(profileRef, { page: next, pageSize: 25 });
    if (!tRes || tRes.error) return;
    setTimeline((prev) => ({
      ...tRes,
      items: [...(prev?.items || []), ...(tRes.items || [])],
    }));
    setTimelinePage(next);
  };

  const onAck = async (ruleId) => {
    const res = await acknowledgeRecommendation(profileRef, ruleId);
    if (!res || res.error) {
      message.error(resolveApiErrorMessage(res, t, "common.general_error"));
      return;
    }
    message.success(
      t("customer_365.acked", "Recommendation marked as handled")
    );
    setRecommendations((prev) =>
      prev.map((r) =>
        r.rule_id === ruleId ? { ...r, acknowledged: true } : r
      )
    );
  };

  const onWhatsApp = async (rec) => {
    if (!profile?.primary_phone) {
      message.warning(
        t(
          "customer_365.missing_phone",
          "Add a phone number to this profile before sending WhatsApp."
        )
      );
      return;
    }
    const key = `wa-${rec.rule_id}`;
    setOutreachBusy(key);
    try {
      const res = await startOutreach(profileRef, {
        channel: "whatsapp",
        message: rec.recommended_action,
        recommendationRuleId: rec.rule_id,
      });
      if (!res || res.error) {
        message.error(
          resolveApiErrorMessage(res, t, "customer_365.outreach_failed")
        );
        return;
      }
      const status = res.status || res.activity?.status;
      message.success(
        t("customer_365.whatsapp_started", "WhatsApp action sent to Sonic") +
          (status ? ` (${status})` : "")
      );
    } finally {
      setOutreachBusy(null);
    }
  };

  const searchMergeCandidates = async () => {
    const term = mergeQuery.trim();
    if (!term) return;
    setMergeLoading(true);
    try {
      const res = await listProfiles({ search: term, pageSize: 10 });
      const list = (res?.profiles || []).filter(
        (p) => (p.gpuuid || String(p.id)) !== (profile?.gpuuid || profileRef)
      );
      setMergeCandidates(list);
    } finally {
      setMergeLoading(false);
    }
  };

  const confirmMerge = (otherRef) => {
    Modal.confirm({
      title: t("customer_365.merge_confirm_title", "Merge profiles?"),
      content: t(
        "customer_365.merge_confirm_body",
        "Signals and identifiers from the selected profile will move into this profile. This cannot be undone easily."
      ),
      onOk: async () => {
        const res = await mergeProfiles(profileRef, otherRef);
        if (!res || res.error) {
          message.error(
            resolveApiErrorMessage(res, t, "common.general_error")
          );
          return;
        }
        message.success(t("customer_365.merged", "Profiles merged"));
        setMergeOpen(false);
        load();
      },
    });
  };

  const drawerReviews = useMemo(
    () => buildTimelineDrawerReviews(timeline?.items || []),
    [timeline?.items],
  );
  const { highlights: drawerHighlights } = useWelcomeDrawerReviewHighlights(
    drawerReviews,
    { enabled: drawerReviews.length > 0, notifyOnError: false },
  );

  if (loading) {
    return (
      <div className={embedded ? styles.drawerLoading : styles.page}>
        <Spin />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={embedded ? styles.drawerBody : styles.page}>
        <Empty />
        {!embedded ? (
          <Link to="/console/customer-365" className={styles.backLink}>
            <ArrowLeftOutlined /> {t("customer_365.back", "Back to list")}
          </Link>
        ) : null}
      </div>
    );
  }

  const trend = profile.recent_sentiment_trend || {};
  const activeRecs = recommendations.filter((r) => !r.acknowledged);
  const hasMore =
    timeline && (timeline.items || []).length < (timeline.total || 0);
  const profileInsights = aggregateReviewAnalysis(timeline?.items || []);
  const hasProfileInsights =
    profileInsights.summary ||
    profileInsights.aggregated_intent?.total > 0 ||
    profileInsights.root_causes.length > 0 ||
    profileInsights.key_drivers.length > 0 ||
    profileInsights.highlights.length > 0 ||
    profileInsights.topics.length > 0;

  return (
    <div className={embedded ? styles.drawerBody : styles.page}>
      {!embedded ? (
        <Link to="/console/customer-365" className={styles.backLink}>
          <ArrowLeftOutlined /> {t("customer_365.back", "Back to list")}
        </Link>
      ) : null}

      {!embedded ? (
        <div className={styles.header}>
          <h1 className={styles.title}>
            {profile.gpuuid || `#${profile.id}`}
          </h1>
        </div>
      ) : null}

      <Collapse ghost className={styles.panel}>
        <Collapse.Panel
          header={t("customer_365.identity", "Identity")}
          key="identity"
        >
          <div className={styles.metaGrid}>
            <div>
              <span className={styles.metaLabel}>
                {t("customer_365.col_ticket_id", "Ticket ID")}
              </span>
              {profile.ticket_id || "—"}
            </div>
            <div>
              <span className={styles.metaLabel}>
                {t("customer_365.col_user_id", "User ID / Username")}
              </span>
              {profile.user_id || "—"}
            </div>
            <div>
              <span className={styles.metaLabel}>
                {t("customer_365.col_name", "Name")}
              </span>
              {profile.display_name || "—"}
            </div>
            <div>
              <span className={styles.metaLabel}>
                {t("customer_365.col_email", "Email")}
              </span>
              {profile.primary_email || "—"}
            </div>
            <div>
              <span className={styles.metaLabel}>
                {t("customer_365.col_phone", "Phone")}
              </span>
              {profile.primary_phone || "—"}
            </div>
          </div>
        </Collapse.Panel>
      </Collapse>

      <div className={styles.panel}>
        <div
          className={styles.toolbar}
          style={{ justifyContent: "space-between" }}
        >
          <h3 className={styles.panelTitle} style={{ margin: 0 }}>
            {t("customer_365.summary", "Summary")}
          </h3>
          <Button onClick={() => setMergeOpen(true)}>
            {t("customer_365.merge", "Merge profiles")}
          </Button>
        </div>
        <div className={styles.metaGrid}>
          <div>
            <span className={styles.metaLabel}>First seen</span>
            {profile.first_seen_at
              ? formatAppDateTime(profile.first_seen_at, i18n.language) || "—"
              : "—"}
          </div>
          <div>
            <span className={styles.metaLabel}>Last seen</span>
            {profile.last_seen_at
              ? formatAppDateTime(profile.last_seen_at, i18n.language) || "—"
              : "—"}
          </div>
          <div>
            <span className={styles.metaLabel}>
              {t("customer_365.col_reviews", "Reviews")}
            </span>
            {profile.signal_count ?? 0}
          </div>
          <div>
            <span className={styles.metaLabel}>Avg sentiment</span>
            {profile.avg_sentiment ?? "—"}
          </div>
          <div>
            <span className={styles.metaLabel}>30d / 90d</span>
            {trend.avg_30d ?? "—"} / {trend.avg_90d ?? "—"}
          </div>
          <div>
            <span className={styles.metaLabel}>90d delta</span>
            {trend.delta_90d_vs_prev ?? "—"}
          </div>
          <div>
            <span className={styles.metaLabel}>Trend</span>
            <SentimentSparkline series={profile.sentiment_series || []} />
          </div>
        </div>
      </div>

      <div className={styles.panel}>
        <h3 className={styles.panelTitle}>
          {t("customer_365.recommendations", "Recommendations")}
        </h3>
        {activeRecs.length === 0 ? (
          <div style={{ color: "var(--color4)" }}>
            {t("customer_365.no_recs", "No active recommendations.")}
          </div>
        ) : (
          activeRecs.map((rec) => (
            <div
              key={rec.rule_id}
              className={`${styles.recoItem} ${
                rec.priority === "high"
                  ? styles.priorityHigh
                  : rec.priority === "low"
                    ? styles.priorityLow
                    : styles.priorityMedium
              }`}
            >
              <div>
                <Tag>{rec.priority}</Tag>
                <strong>{rec.name}</strong>
                <div style={{ marginTop: 6 }}>{rec.recommended_action}</div>
              </div>
              <Space className={styles.recoActions} wrap>
                <Button
                  size="small"
                  type="primary"
                  icon={<WhatsAppOutlined />}
                  loading={outreachBusy === `wa-${rec.rule_id}`}
                  disabled={!profile?.primary_phone}
                  onClick={() => onWhatsApp(rec)}
                >
                  {t("customer_365.send_whatsapp", "Send WhatsApp")}
                </Button>
                <Tooltip
                  title={t(
                    "customer_365.voice_coming_soon",
                    "AI voice call from Customer 365 is coming soon"
                  )}
                >
                  <Button size="small" icon={<PhoneOutlined />} disabled>
                    {t("customer_365.voice_call", "Voice call")}
                  </Button>
                </Tooltip>
                <Button size="small" onClick={() => onAck(rec.rule_id)}>
                  {t("customer_365.ack", "Mark handled")}
                </Button>
              </Space>
            </div>
          ))
        )}
      </div>

      {hasProfileInsights ? (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>
            {t("customer_365.insights", "Insights")}
          </h3>
          <ReviewAnalysisPanel analysis={profileInsights} compact />
        </div>
      ) : null}

      <div className={styles.panel}>
        <h3 className={styles.panelTitle}>
          {t("customer_365.timeline", "Timeline")}
          {timeline?.total != null ? ` (${timeline.total})` : ""}
        </h3>
        {(timeline?.items || []).length === 0 ? (
          <Empty
            description={t("customer_365.no_signals", "No signals yet")}
          />
        ) : (
          <>
            {(timeline.items || []).map((item) => {
              const drawerReview = timelineItemToDrawerReview(item);
              const highlightEntry =
                drawerReview?.date_gpid != null
                  ? drawerHighlights[drawerReview.date_gpid]
                  : null;
              return (
                <div key={item.id} className={styles.timelineItem}>
                  <div className={styles.channelIcon}>
                    {channelIcon(item)}
                  </div>
                  <div className={styles.timelineContent}>
                    {drawerReview ? (
                      <WelcomeDrawerSimpleReviewCard
                        review={drawerReview}
                        highlightEntry={highlightEntry}
                        theme={theme}
                      />
                    ) : null}
                    {item.review_analysis ? (
                      <ReviewAnalysisPanel analysis={item.review_analysis} />
                    ) : null}
                  </div>
                </div>
              );
            })}
            {hasMore ? (
              <Button style={{ marginTop: 12 }} onClick={loadMoreTimeline}>
                {t("customer_365.load_more", "Load more")}
              </Button>
            ) : null}
          </>
        )}
      </div>

      <Modal
        open={mergeOpen}
        title={t("customer_365.merge", "Merge profiles")}
        onCancel={() => setMergeOpen(false)}
        footer={null}
      >
        <Input.Search
          placeholder={t(
            "customer_365.merge_search",
            "Search by GPUUID, email, ticket, or user ID"
          )}
          enterButton
          loading={mergeLoading}
          value={mergeQuery}
          onChange={(e) => setMergeQuery(e.target.value)}
          onSearch={searchMergeCandidates}
        />
        <div style={{ marginTop: 12 }}>
          {mergeCandidates.map((c) => (
            <div
              key={c.gpuuid || c.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <span>{c.gpuuid || `#${c.id}`}</span>
              <Button
                size="small"
                type="primary"
                onClick={() => confirmMerge(c.gpuuid || c.id)}
              >
                {t("customer_365.merge_into_this", "Merge into this")}
              </Button>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default ProfileDetailView;
